"use client";

import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ExternalLink, Github, ChevronLeft, ChevronRight } from "lucide-react";
import { projects } from "../../utils/projectInfo";
import { imagepaths } from "../../utils/image";
import { Button } from "../../utils/button";
import { Badge } from "../../utils/badge";

export default function HorizontalProjectShowcase() {
  const [expandedProject, setExpandedProject] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef(null);
  const projectRefs = useRef({});

  const toggleProject = (projectId) => {
    setExpandedProject(expandedProject === projectId ? null : projectId);
  };

  const handleKeyDown = (e, projectId) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleProject(projectId);
    }
  };

  const cardWidth = 325;
  const gap = 24; // gap-6 in px

  const scrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -cardWidth - gap, behavior: "smooth" });
  };

  const scrollRight = () => {
    scrollRef.current?.scrollBy({ left: cardWidth + gap, behavior: "smooth" });
  };

  // Update active dot index based on scroll position
  const onScroll = () => {
    if (!scrollRef.current) return;
    const scrollLeftPos = scrollRef.current.scrollLeft;
    const index = Math.round(scrollLeftPos / (cardWidth + gap));
    setActiveIndex(index);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener("scroll", onScroll, { passive: true });

    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="container font-rubik mx-auto p-4 max-w-7xl mt-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="bg-gradient-to-tr from-peach to-peachLite bg-clip-text text-transparent text-4xl md:text-5xl font-medium mb-2">
          Featured Projects
        </h1>
        {/* <p className="text-blue text-lg">
          Discover my latest work and creative solutions
        </p> */}
      </div>

      {/* Navigation Controls */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-2">
          <motion.button
            onClick={scrollLeft}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="p-2 rounded-full bg-white shadow-lg border border-gray-200 text-blue hover:bg-gray-50 transition-colors"
            aria-label="Scroll left"
          >
            <ChevronLeft className="w-5 h-5" />
          </motion.button>
          <motion.button
            onClick={scrollRight}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="p-2 rounded-full bg-white shadow-lg border border-gray-200 text-blue hover:bg-gray-50 transition-colors"
            aria-label="Scroll right"
          >
            <ChevronRight className="w-5 h-5" />
          </motion.button>
        </div>
        <div className="text-sm text-gray-500">{projects.length} Projects</div>
      </div>

      {/* Horizontal Scroll Container */}
      <div className="relative">
        <div
          ref={scrollRef}
          className="flex gap-6 overflow-x-auto pb-6 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
          style={{ scrollbarWidth: "thin" }}
          tabIndex={0}
          aria-label="Project list horizontally scrollable"
        >
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="w-[325px] flex-shrink-0"
              ref={(el) => (projectRefs.current[project.id] = el)}
              id={project.id}
            >
              <div className="border rounded-lg overflow-hidden shadow-lg bg-white h-full flex flex-col">
                <motion.div
                  className="relative cursor-pointer"
                  onClick={() => toggleProject(project.id)}
                  onKeyDown={(e) => handleKeyDown(e, project.id)}
                  tabIndex={0}
                  role="button"
                  aria-expanded={expandedProject === project.id}
                  whileHover="hover"
                >
                  <div className="relative overflow-hidden">
                    <motion.img
                      src={imagepaths(project.image)}
                      alt={project.title}
                      className="w-full h-48 object-cover"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.3 }}
                    />
                    <motion.div
                      className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center"
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <span className="text-white text-lg font-semibold">
                        {expandedProject === project.id
                          ? "Hide Details"
                          : "View Details"}
                      </span>
                    </motion.div>
                  </div>
                </motion.div>

                <div className="p-4 flex-grow">
                  <h2 className="bg-gradient-to-tr from-peach to-peachLite bg-clip-text text-transparent text-xl font-semibold mb-2">
                    {project.title}
                  </h2>
                  <p className="text-blue mb-4 line-clamp-3">
                    {project.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.technologies.slice(0, 3).map((tech) => (
                      <Badge key={tech} variant="secondary">
                        {tech}
                      </Badge>
                    ))}
                    {project.technologies.length > 3 && (
                      <Badge variant="secondary">
                        +{project.technologies.length - 3} more
                      </Badge>
                    )}
                  </div>
                </div>

                <AnimatePresence>
                  {expandedProject === project.id && (
                    <motion.div
                      key={`expanded-${project.id}`}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="p-4 bg-gray-50 border-t"
                    >
                      <h3 className="bg-gradient-to-tr from-peach to-peachLite bg-clip-text text-transparent text-lg font-semibold mb-2">
                        Key Features
                      </h3>
                      <ul className="list-disc list-inside mb-4 space-y-1">
                        {project.features.map((feature, idx) => (
                          <motion.li
                            key={idx}
                            className="text-blue text-sm"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3, delay: idx * 0.1 }}
                          >
                            {feature}
                          </motion.li>
                        ))}
                      </ul>

                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                          Technologies Used:
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {project.technologies.map((tech) => (
                            <Badge key={tech} variant="secondary">
                              {tech}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-4">
                        {project.live && (
                          <Button variant="outline" asChild>
                            <motion.a
                              href={project.demoLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <ExternalLink className="w-4 h-4 mr-2" />
                              Live Demo
                            </motion.a>
                          </Button>
                        )}
                        <Button variant="contained" asChild>
                          <motion.a
                            href={project.githubLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <Github className="w-4 h-4 mr-2" />
                            View Code
                          </motion.a>
                        </Button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="flex justify-center mt-4">
        <div className="flex gap-2">
          {projects.map((_, idx) => (
            <div
              key={idx}
              className={`w-2 h-2 rounded-full transition-colors cursor-pointer ${
                idx === activeIndex ? "bg-peach" : "bg-gray-300"
              }`}
              aria-label={`Scroll indicator for project ${idx + 1}`}
              onClick={() => {
                scrollRef.current?.scrollTo({
                  left: idx * (cardWidth + gap),
                  behavior: "smooth",
                });
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
